/**
* Copyright by:

* by ESS-Ehrhardt Software Solutions & Service GmbH
* Basselscheider Str. 55
* D-56281 Emmelshausen
* +49 (0) 67 42 / 87 27 0
* info@epg.com
*/
import { CommonModule } from '@angular/common';
import { NgModule } from '@angular/core';
import { ExtraOptions, RouterModule } from '@angular/router';
import { loggedInGuard } from '@epg/angular-base-lib';
import {
  EpgForbiddenComponent,
  EpgFrameComponent,
  EpgIconData,
  EpgPrivacyPolicyComponent,
  EpgRoutes,
  permissionsGuard
} from '@epg/ng-components';
import {
  faCalendar,
  faChartBar,
  faClipboardListCheck,
  faFolder,
  faImages,
  faTasks,
  faUser,
  faUsers
} from '@fortawesome/pro-solid-svg-icons';
import { OneAppPermission } from '../enums/permission.enum';
import { CandeactivateGuard } from '../services/guards/candeactivate.guard';
import { GalleryComponent } from './home/gallery/gallery.component';
import { HomeComponent } from './home/home.component';
import { ScheduledTaskDetailComponent } from './home/task-home/scheduled-task/scheduled-task-detail/scheduled-task-detail.component';
import { ScheduledTaskComponent } from './home/task-home/scheduled-task/scheduled-task.component';
import { TaskHomeComponent } from './home/task-home/task-home.component';
import { TaskDetailComponent } from './home/task-home/task/task-detail/task-detail.component';
import { TaskManagerComponent } from './home/task-home/task/task-manager/task-manager.component';
import { TaskComponent } from './home/task-home/task/task.component';
import { TasksResolver } from './home/task-home/tasks-resolver.component';
import { EmployeeDashboardComponent } from './home/time-tracking/employee-dashboard/employee-dashboard.component';
import { TaskDashboardComponent } from './home/time-tracking/task-dashboard/task-dashboard.component';
import { TimeTrackingComponent } from './home/time-tracking/time-tracking.component';
import { WorkflowDashboardComponent } from './home/time-tracking/workflow-dashboard/workflow-dashboard.component';
import { WorkflowDesignerPlaceholderComponent } from './home/workflow/workflow-designer-placeholder/workflow-designer-placeholder.component';
import { WorkflowDetailComponent } from './home/workflow/workflow-detail/workflow-detail.component';
import { WorkflowComponent } from './home/workflow/workflow.component';
import { LoginComponent } from './login/login/login.component';
import { EmployeeGroupDetailComponent } from './setting/employee-group/employee-group-detail/employee-group-detail.component';
import { EmployeeGroupComponent } from './setting/employee-group/employee-group.component';
import { EmployeeDetailComponent } from './setting/employee/employee-detail/employee-detail.component';
import { EmployeeComponent } from './setting/employee/employee.component';
import { SettingComponent } from './setting/setting.component';

const routes: EpgRoutes = [
  {
    path: '',
    redirectTo: 'home',
    pathMatch: 'full'
  },
  {
    path: 'home',
    canActivate: [loggedInGuard, permissionsGuard],
    component: HomeComponent,
    data: {
      menuItem: EpgFrameComponent.MENU_ITEM_HOME,
      requiredPermissions: OneAppPermission.WEB_USE
    },
    children: [
      {
        path: '',
        redirectTo: 'workflow',
        pathMatch: 'full'
      },
      {
        path: 'workflow',
        component: WorkflowComponent,
        data: {
          menuItem: {
            name: $localize`:@@sideMenuWorkflow:Workflows`,
            icon: new EpgIconData(faFolder)
          }
        },
        children: [
          {
            path: ':workflowId',
            component: WorkflowDetailComponent,
            canDeactivate: [CandeactivateGuard],
            data: {
              menuItem: {
                isHidden: true
              }
            }
          }
        ]
      },
      {
        path: 'task',
        children: [
          { path: '', pathMatch: 'full', component: TasksResolver },
          {
            path: ':taskId',
            component: TasksResolver,
            data: {
              menuItem: {
                isHidden: true
              }
            }
          },
          { path: 'task-manager', redirectTo: '/home/tasks/activities/task-manager' }
        ]
      },
      {
        path: 'tasks',
        component: TaskHomeComponent,
        data: {
          menuItem: {
            name: $localize`:@@sideSubMenuTasks:Tasks`,

            icon: new EpgIconData(faTasks)
          }
        },
        children: [
          { path: '', redirectTo: 'activities', pathMatch: 'full' },
          {
            path: 'activities',
            component: TaskComponent,
            data: {
              menuItem: {
                name: $localize`:@@sideSubMenuActivities:Activities`,
                icon: new EpgIconData(faClipboardListCheck)
              }
            },
            children: [
              { path: 'task-manager', component: TaskManagerComponent },
              {
                path: ':taskId',
                component: TaskDetailComponent,
                data: {
                  menuItem: {
                    isHidden: true
                  }
                }
              }
            ]
          },
          {
            path: 'scheduled',
            component: ScheduledTaskComponent,
            data: {
              menuItem: {
                name: $localize`:@@sideSubMenuScheduled:Scheduled`,
                icon: new EpgIconData(faCalendar)
              }
            },
            children: [
              {
                path: ':scheduledTaskId',
                component: ScheduledTaskDetailComponent,
                data: {
                  menuItem: {
                    isHidden: true
                  }
                }
              }
            ]
          }
        ]
      },
      {
        path: 'workflow/:id/designer',
        component: WorkflowDesignerPlaceholderComponent,
        canDeactivate: [CandeactivateGuard],
        data: {
          useSidebar: false,
          menuItem: {
            isHidden: true
          }
        }
      },
      {
        path: 'time-tracking',
        component: TimeTrackingComponent,
        data: {
          menuItem: {
            name: $localize`:@@sideMenuTimeTrackig:Dashboard`,
            icon: new EpgIconData(faChartBar)
          }
        },
        children: [
          {
            path: 'workflow/:workflowId/:fromDate/:toDate/:fromTime/:toTime',
            component: WorkflowDashboardComponent
          },
          {
            path: 'workflow/:workflowId/employee/:employeeId/:fromDate/:toDate/:fromTime/:toTime',
            component: EmployeeDashboardComponent
          },
          {
            path: 'workflow/:workflowId/employee/:employeeId/task/:taskId/:fromDate/:toDate/:fromTime/:toTime',
            component: TaskDashboardComponent
          }
        ]
      },
      {
        path: 'gallery',
        component: GalleryComponent,
        data: {
          menuItem: {
            name: $localize`:@@sideMenuGallery:Gallery`,
            icon: new EpgIconData(faImages)
          }
        }
      }
    ]
  },
  {
    path: 'Administration',
    component: SettingComponent,
    data: {
      menuItem: EpgFrameComponent.MENU_ITEM_ADMINISTRATION,
      requiredPermissions: OneAppPermission.WEB_USE
    },
    canActivate: [loggedInGuard, permissionsGuard],
    children: [
      { path: '', redirectTo: 'employee', pathMatch: 'full' },
      {
        path: 'employee',
        component: EmployeeComponent,
        data: {
          menuItem: {
            name: $localize`:@@employee:Employee`,
            icon: new EpgIconData(faUser)
          }
        },
        children: [
          {
            path: ':employeeId',
            component: EmployeeDetailComponent,
            canDeactivate: [CandeactivateGuard],
            data: {
              menuItem: {
                isHidden: true
              }
            }
          },
          { path: 'new', component: EmployeeDetailComponent, canDeactivate: [CandeactivateGuard] }
        ]
      },
      {
        path: 'employee-group',
        component: EmployeeGroupComponent,
        data: {
          menuItem: {
            name: $localize`:@@employeeGroupsLabel:Employee Groups`,
            icon: new EpgIconData(faUsers)
          }
        },
        children: [
          {
            path: ':employeeGroupId',
            component: EmployeeGroupDetailComponent,
            canDeactivate: [CandeactivateGuard],
            data: {
              menuItem: {
                isHidden: true
              }
            }
          },
          {
            path: 'new',
            component: EmployeeGroupDetailComponent,
            canDeactivate: [CandeactivateGuard]
          }
        ]
      }
    ]
  },
  {
    path: 'privacy-policy',
    data: {
      menuItem: { isHidden: true }
    },
    children: [
      {
        path: '',
        pathMatch: 'full',
        component: EpgPrivacyPolicyComponent,
        data: {
          menuItem: EpgPrivacyPolicyComponent.MENU_ITEM_PRIVACY_POLICY
        }
      }
    ]
  },
  {
    path: 'forbidden',
    data: {
      useSidebar: false,
      menuItem: { isHidden: true }
    },
    component: EpgForbiddenComponent
  },
  { path: '**', component: LoginComponent }
];

const routerOptions: ExtraOptions = {
  scrollPositionRestoration: 'enabled',
  anchorScrolling: 'enabled',
  scrollOffset: [0, 64],
  onSameUrlNavigation: 'reload'
};

@NgModule({
  imports: [CommonModule, RouterModule.forRoot(routes, routerOptions)],
  exports: [RouterModule],
  providers: [
    // {
    //   provide: 'WebPermissionGuardTest',
    //   deps: [TenantMappingService, LogService, TenantService, Router],
    //   useFactory: async (
    //     logService: LogService,
    //     tenantMapper: TenantMappingService,
    //     tenantService: TenantService,
    //     router: Router
    //   ) => {
    //     let tenant = (await tenantMapper.getActiveTenant()) ?? '';
    //     return new WebPermissionGuard(tenant, logService, tenantMapper, tenantService, router);
    //   }
    // }
  ]
})
export class AppRoutingModule {}
